import { OrderType } from 'apiConnector/secondary-market/batch/model';
import { ColumnGroupPreferenceState, ColumnPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { useAppSelector } from 'hooks/useAppSelector';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { cloneDeep, isEqual } from 'lodash-es';
import { useEffect, useMemo } from 'react';
import { lastValueFrom } from 'rxjs';
import { secondaryMarketBatchActions } from 'state/workspaces/secondary-market/batch/actions';
import { selectPreferenceState, selectStateByOrderType } from 'state/workspaces/secondary-market/batch/selectors';
import { batchApi, makeSureIsLoadedForAllOrderTypesThunk } from 'state/workspaces/secondary-market/batch/thunks';

export function useGridStateByOrderType(orderType: OrderType) {
  const dispatch = useThunkDispatch();
  const stateByOrderType = useAppSelector(s => selectStateByOrderType(s, orderType));
  const status = stateByOrderType.status;
  useEffect(() => {
    dispatch(makeSureIsLoadedForAllOrderTypesThunk());
  }, [dispatch]);

  return useMemo(() => {
    const result = stateByOrderType.status === 'success' ? stateByOrderType : undefined;
    return {
      status,
      error: stateByOrderType.status === 'error' ? stateByOrderType.error : undefined,
      data: result?.content.rows.map(d => cloneDeep(d.data)),
    };
  }, [stateByOrderType, status]);
}

export const usePreferenceByOrderType = (orderType: OrderType) => {
  const preference = useAppSelector(s => selectPreferenceState(s, orderType));
  const dispatch = useThunkDispatch();
  return useMemo(
    () => ({
      columnState: preference?.columnState,
      columnGroupState: preference?.columnGroupState,
      onPreferenceChanged: async (
        columnState: readonly ColumnPreferenceState[],
        columnGroups?: readonly ColumnGroupPreferenceState[],
      ) => {
        const preferences = { columnGroupState: columnGroups, columnState: columnState };
        if (isEqual(preference?.columnState, columnState) && isEqual(preference?.columnGroupState, columnGroups)) {
          return;
        }
        dispatch(secondaryMarketBatchActions.updatePreference(orderType, preferences));
        await lastValueFrom(batchApi.saveState(orderType, preferences), {
          defaultValue: undefined,
        });
      },
    }),
    [preference, dispatch, orderType],
  );
};
